import {
  BookOpen,
  CalendarDays,
  HeartHandshake,
  MessageCircle,
  Phone,
  Play,
  Radio,
  ShieldAlert,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import type { PlaybackContinue } from "../api/types";
import { CommandButton } from "../components/CommandButton";
import { PageShell } from "../components/PageShell";
import {
  DEFAULT_ERROR_RECOVERY_ACTION,
  StatusBlock,
} from "../components/StatusBlock";
import { useAudioCoordinator } from "../stores/audioCoordinator";
import { useNavigationStore } from "../stores/navigationStore";
import { useSessionStore } from "../stores/sessionStore";
import styles from "../styles/App.module.css";

const OFFLINE_MAIN_MENU_MESSAGE =
  "当前离线，仅显示主菜单。播放、留言、求助和报平安不会离线排队，请恢复网络后手动重试。";
const OFFLINE_MAIN_MENU_RECOVERY =
  "请恢复网络后再双击需要的菜单；紧急求助请直接用电话联系家人。";

export function HomePage() {
  const go = useNavigationStore((state) => state.go);
  const mainMenuFocusRequest = useNavigationStore(
    (state) => state.mainMenuFocusRequest,
  );
  const play = useAudioCoordinator((state) => state.play);
  const announce = useAudioCoordinator((state) => state.announce);
  const offlineMainMenuShell = useSessionStore(
    (state) => state.offlineMainMenuShell,
  );
  const [resume, setResume] = useState<PlaybackContinue | null>(null);
  const [familyMessagesVisible, setFamilyMessagesVisible] = useState(false);
  const [error, setError] = useState("");
  const commandListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (offlineMainMenuShell) {
      setResume(null);
      setFamilyMessagesVisible(false);
      setError("");
      return;
    }

    void api
      .playbackContinue()
      .then(setResume)
      .catch((loadError) =>
        setError(
          loadError instanceof Error ? loadError.message : "读取继续播放失败。",
        ),
      );
    void api
      .messages()
      .then((result) => setFamilyMessagesVisible(result.items.length > 0))
      .catch(() => setFamilyMessagesVisible(false));
  }, [offlineMainMenuShell]);

  useEffect(() => {
    if (!offlineMainMenuShell) {
      api.prefetchCorePageData();
    }
  }, [offlineMainMenuShell]);

  useEffect(() => {
    if (mainMenuFocusRequest <= 0) {
      return;
    }
    commandListRef.current?.querySelector("button")?.focus();
  }, [mainMenuFocusRequest]);

  return (
    <PageShell title="主菜单" where="主菜单">
      {offlineMainMenuShell ? (
        <StatusBlock
          tone="error"
          message={OFFLINE_MAIN_MENU_MESSAGE}
          recoveryAction={OFFLINE_MAIN_MENU_RECOVERY}
        />
      ) : null}
      {error ? (
        <StatusBlock
          tone="error"
          message={error}
          recoveryAction={DEFAULT_ERROR_RECOVERY_ACTION}
        />
      ) : null}
      <div
        ref={commandListRef}
        className={styles.commandList}
        aria-label="主菜单"
      >
        <CommandButton
          icon={Play}
          title="继续上次播放"
          hint={
            offlineMainMenuShell
              ? "当前离线，恢复网络后再试"
              : resume
                ? `${resume.title}，从上次位置继续`
                : "正在读取上次播放"
          }
          onClick={() => {
            if (offlineMainMenuShell) {
              announce("当前离线，仅显示主菜单。请恢复网络后再继续播放。");
              return;
            }
            if (!resume) {
              announce(
                error
                  ? "继续播放暂时不可用，请进入听戏和评书，或稍后再试。"
                  : "正在读取上次播放，请稍等。",
              );
              return;
            }
            void play({
              title: resume.title,
              episodeId: resume.episode_id,
              seriesId: resume.series_id,
              contentType: resume.content_type,
              startAtSeconds: resume.position_seconds,
              grant: () => api.mediaGrant(resume.episode_id),
            });
          }}
        />
        <CommandButton
          icon={BookOpen}
          title="听戏和评书"
          onClick={() => go({ name: "media" })}
        />
        {familyMessagesVisible ? (
          <CommandButton
            icon={MessageCircle}
            title="听家人留言"
            onClick={() => go({ name: "messages" })}
          />
        ) : null}
        <CommandButton
          icon={Radio}
          title="听电台"
          onClick={() => go({ name: "radio" })}
        />
        <CommandButton
          icon={Phone}
          title="联系家人"
          onClick={() => go({ name: "contacts" })}
        />
        <CommandButton
          icon={CalendarDays}
          title="今天信息"
          onClick={() => go({ name: "today" })}
        />
        <CommandButton
          icon={HeartHandshake}
          title="报平安"
          onClick={() => go({ name: "peace" })}
        />
        <CommandButton
          icon={ShieldAlert}
          title="请求帮助"
          onClick={() => go({ name: "help" })}
        />
      </div>
    </PageShell>
  );
}
